import {
  AfterViewInit,
  Component,
  ElementRef,
  HostBinding,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { LMFieldBaseComponent } from '@field/lm-field-base.component';
import { LMModel, LMModelChangedEvent } from '@form/model';
import { SohoDropDownComponent } from 'ids-enterprise-ng';
import { LMFormRequestService } from '@form/services';
import { LMFieldServiceAdapter } from '@field/lm-field-service-adapter';
import * as urltemplate from 'url-template';
import { LMLayoutType } from '@field/services/lm-field-layout.service';
import { AppConstants } from '@utils/app.constants';
import { Observable } from 'rxjs/internal/Observable';
import { LMKeyType } from '@app/services';

enum LMDropDownDisplayTypeEnum {
  PLAIN = 0,
  USER_DEFINED,
  MULTI,
  TEXT,
  BOOLEAN,
  TAG
}

@Component({
  selector: 'lm-dropdown',
  templateUrl: 'lm-dropdown.component.html',
  styleUrls: [ 'lm-dropdown.component.scss' ]
})
export class LMDropdownComponent extends LMFieldBaseComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(SohoDropDownComponent) dropdown: SohoDropDownComponent;

  @Input() layoutObject: LMDropdownLayout;
  @Input() specFields: LMSpecFields;
  @Input() model: LMModel;

  @HostListener('keyup', [ '$event' ]) onKeyUp(event) {
    if (event.which === 13 && event.target.nodeName !== 'BUTTON') {
      event.stopPropagation();
      event.stopImmediatePropagation();
      event.preventDefault();
    }
  }

  @HostBinding('class.keyfield-multi-select') get keyMultiSelectClass() {
    return this.modelField && this.modelField.isKeyFieldMultiselect;
  }

  @HostBinding('class.lm-dropdown-is-tag') get classDropDownIsTag() {
    return this.displayAsTag;
  }

  @HostBinding('attr.data-automation-id') get dataFieldAutomationId() {
    return `lm-${this.fieldAutomationId}-field`;
  }

  // @HostBinding('attributes') dropdownAttributes: SohoAttributesArray;
  public dropdownAttributes: SohoAttributesArray = [];

  public isMultiSelect = false;
  public isUserDefined: boolean;
  public isBoolean = false;
  public showDropDownDirtyIndicator = false;
  public userDefinedReloadType: SohoDropDownReloadStyles;

  public displayType: LMDropDownDisplayTypeEnum;
  public layoutDisplayType;
  public maxSelected: number;
  public showUserDefinedAsMulti = false;
  // public showSelectAll: boolean;

  constructor(
    private lmFormRequestService: LMFormRequestService,
    protected lmFieldServiceAdapter: LMFieldServiceAdapter,
    private element: ElementRef
  ) {
    super(lmFieldServiceAdapter);
    this.layoutDisplayType = LMDropDownDisplayTypeEnum;
  }

  // events
  ngOnInit() {
    if (this.lmFieldServiceAdapter.lmLoggerService.cskLog === 2) {
      this.lmFieldServiceAdapter.lmLoggerService.time({
        id: `Total-Dropdown-${this.instanceId}`,
        description: 'TOTAL DROPDOWN',
        functionName: 'ngOnInit()',
        className: 'LMDropdownComponent',
        timingStart: 'LMDropdownComponent -> ngOnInit()',
        timingEnd: 'LMDropdownComponent -> ngAfterViewInit()',
        fieldType: this.layoutObject.type
      });
    }

    super.ngOnInit();
    this.initialize(this.layoutObject, this.specFields, this.model);

    if (this.layoutType === LMLayoutType.MULTI_SELECT) {
      this.isMultiSelect = true;
    }

    this.isUserDefined = this.specField?.hasUserDefinedStates;
    this.isBoolean = this.onSearchForm && this.specField?.type === AppConstants.BOOLEAN;

    // // This is need when we use a filter and then want to refine the search
    if (this.onSearchForm && this.isUserDefined && !this.modelField.isBlank ) {
      // need to get the states because the search field has a value.
      this.prepareUserDefinedRequest('').subscribe((result: LMUserDefinedResponse) => {
        this.handleUserDefinedResponse(result);
      },
      (error) => {
        this.lmFieldServiceAdapter.lmLoggerService.error([ 'Error in getUserDefinedStates', this.bindId, error ]);
      });
    }

    if (this.lmFieldServiceAdapter.lmContextService.userDefinedStatesPageSizeAndIndicator()) {
      this.userDefinedReloadType = 'typeahead';
    } else {
      this.userDefinedReloadType = 'open';
    }

    if (!this.onSearchForm) {
      if (!(this.displayAsText || this.displayAsTag)) {
        this.subscribeToInlineError();
      }

      if (this.displayAsTag) {
        this.displayType = this.layoutDisplayType.TAG;

      } else if (this.displayAsText) {
        this.displayType = this.layoutDisplayType.TEXT;

      } else if (this.isMultiSelect) {
        this.displayType = this.layoutDisplayType.MULTI;
        if (this.modelField.isKeyFieldMultiselect) {
          this.maxSelected = null;

        } else {
          this.maxSelected = this.modelField.maxSelected;

        }
      } else if (this.isUserDefined) {
        this.displayType = this.layoutDisplayType.USER_DEFINED;

      } else {
        this.displayType = this.layoutDisplayType.PLAIN;
      }

      this.subscriptionArray.push(this.model.modelChanged
        .subscribe((modelChangedEvent: LMModelChangedEvent) => {
          if (modelChangedEvent.eventType === 'update') {
            // LMCLIENT-35595: Special case update required?
            if (this.updateDropDownStates()) {
              this.prepareUserDefinedRequest('', -1)
                .subscribe((result: LMUserDefinedResponse) => {
                  const aryOptions = this.handleUserDefinedResponse(result);
                  const found = aryOptions.find(v => v.value === this.modelField.value);
                  if (!found) {
                    this.modelField.states.push({ value: String(this.modelField.value), label: String(this.modelField.value) });
                  }
                  // needs to be in the next tick to update the dom with new state field values
                  this.lmFieldServiceAdapter.ngZone.runOutsideAngular(() => {
                    setTimeout(() => {
                      this.dropdown.updated();
                    }, 1);
                  });
                });
            }
          }
        })
      );
    } else {
      if (this.specField.isBusClassContextField) {
        this.displayType = this.layoutDisplayType.BOOLEAN;

      } else if (this.model.isCubeSearchForm) {
        // Override the type since cube does not support multi-search yet. The model states will include a blank value.
        // Boolean does not add multiple attribute to drop down. Plain also has multiple attribute set.
        this.displayType = this.layoutDisplayType.BOOLEAN;

      } else if (this.isMultiSelect) {
        this.displayType = this.layoutDisplayType.MULTI;

      } else if (this.isBoolean) {
        this.displayType = this.layoutDisplayType.BOOLEAN;

      } else if (this.isUserDefined) {
        this.displayType = this.layoutDisplayType.USER_DEFINED;
        this.showUserDefinedAsMulti = this.lmFieldServiceAdapter.lmContextService.enableMultipleSearchTerms();

      } else {
        this.displayType = this.layoutDisplayType.PLAIN;

      }
    }

    this.setSize();
    const size = (this.isUserDefined) ? this.specField.size + 2 : this.size;

    // a boolean field does not have a size attribute, so make the dropdown small
    if (this.displayType === this.layoutDisplayType.BOOLEAN) {
      this._fieldClassArray.push('dropdown-xs');
    }

    this.setFieldClasses(size);

    if (this.isTimezoneField) {
      this.previousTimeZone = this.modelField.value;
    }
  }

  ngAfterViewInit() {
    super.ngAfterViewInit();

    // need to set readonly state for initial load
/*    if (this.dropdown) {
      this.dropdown.readonly = this.isReadOnly;
    }*/

    if (this.lmFieldServiceAdapter?.lmLoggerService?.cskLog === 2) {
      this.lmFieldServiceAdapter.lmLoggerService.timeEnd(`Total-Dropdown-${this.instanceId}`);
    }

    //LMCLIENT-34999 enterprise 4.56 prevents keyboard events triggered by function keys to bubble up,
    // added a keydown listener for the field options shortcut.
    this.element?.nativeElement?.querySelector(`select#${this.id} + .dropdown-wrapper > div.dropdown`)?.addEventListener('keydown', (event: KeyboardEvent) => {
      if (this.lmFieldServiceAdapter.lmKeyBoardShortcutService.onKeyDown(event) === LMKeyType.FIELDOPTION) {
        this.openFieldOptions(event);
      }
    });
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }

  onAfterResetDirty(event: SohoTrackDirtyEvent) {
    // only reset dirty to pristine on change if already dirty
    if (this.isDirty) {
      if (this.isCompoundFieldMember && !this.isPreviewCompoundMember) {
        this.showDropDownDirtyIndicator = false;
      }
    }
    super.onAfterResetDirty(event);
  }

  onDirty(event: SohoTrackDirtyEvent) {
    // only set dirty if not already dirty
    if (!this.isDirty) {
      if (this.isCompoundFieldMember && !this.isPreviewCompoundMember) {
        this.showDropDownDirtyIndicator = true;
      }
    }
    super.onDirty(event);
  }

  onPristine(event: SohoTrackDirtyEvent) {
    // only set pristine if already dirty
    if (this.isDirty) {
      if (this.isCompoundFieldMember && !this.isPreviewCompoundMember) {
        this.showDropDownDirtyIndicator = false;
      }
    }
    super.onPristine(event);
  }

  // sohoxi control event; user has interacted with the field and made a selection
  public onChanged(event: Event) {
    this.onChange();

    if (event && this.dropdown) {
      this.doHandleOnFieldChangedForDropdown = true;
    }

    if (this.isTimezoneField) {
      const previousTimeZone = this.previousTimeZone || this.modelField?.originalValue;
      const timeZone = event?.['data'];
      this.model.timeZoneChanged({ bindId: this.bindId, timeZone, previousTimeZone });
      this.previousTimeZone = timeZone;
    }
  }

  source = (response: any, searchTerm: string) => {
    this.busyIndicator = true;

    this.prepareUserDefinedRequest(searchTerm)
      .subscribe((result: LMUserDefinedResponse) => {
        const optionsAry = this.handleUserDefinedResponse(result);
        setTimeout(() => {
          if (typeof response === 'function') {
            // true indicates to sohoxi dropdown to not create option tags
            response(optionsAry, true);
          } else {
            // LMCLIENT-23932: need to call updated() in this case
            this.dropdown.updated();
          }

          this.busyIndicator = false;
        }, 1);

        return;
      },
      (error) => {
        this.busyIndicator = false;
        this.lmFieldServiceAdapter.lmLoggerService.error([ 'Error in getUserDefinedStates', this.bindId, error ]);
      });
  };

  private buildFilterCriteriaQueryString(filterCriteria: Array<LMFilterFieldCriteria>): string {
    // set filter criteria on the url params
    if (filterCriteria) {
      const filterParamString = this.lmFieldServiceAdapter.lmUrlUtilService.filterFieldCriteriaToUrlParams(filterCriteria, 'fc');
      if (filterParamString) {
        return filterParamString;
      }
    }
    return '';
  }

  prepareUserDefinedRequest(searchTerm: string, forcedPagesize?: number): Observable<LMUserDefinedResponse> {
    const selectFieldId = (this.isMultiSelect && !this.modelField.isKeyFieldMultiselect)
      ? this.specField.selectFieldId // change this with LMCLIENT-34628, don't need to send in something like Races.Race[1], should only send in Races.Race this.modelField.members[ 0 ]
      : this.bindId;

    const formUrlInfo = this.model.formUrlInfo;
    const compiledFormRequestUrl = formUrlInfo.formRequestUrl ? urltemplate.parse(formUrlInfo.formRequestUrl) : '';
    let formRequestUrl = (compiledFormRequestUrl) ? compiledFormRequestUrl.expand({ resourceId: this.model.formResourceId, onSearchForm: this.model.isSearchForm }) : '';
    let pageSize: number;

    if (forcedPagesize !== undefined) {
      pageSize = forcedPagesize;
    } else if (this.lmFieldServiceAdapter.lmContextService.userDefinedStatesPageSizeAndIndicator()) {
      if (searchTerm && searchTerm.length > 0) {
        const filterCriteria: LMFilterFieldCriteria = {
          operator: 'equals',
          fieldId: selectFieldId,
          value: searchTerm,
        };

        // could be a case where there are no other csks on the url, so will have to use ? as the delimiter instead of &
        const delimiter = (formRequestUrl.indexOf('?') > -1) ? '&' : '?';
        formRequestUrl += delimiter + this.buildFilterCriteriaQueryString([ filterCriteria ]);
      }
      if (this.model.isSearchForm) {
        formRequestUrl = this.lmFieldServiceAdapter.lmUrlUtilService.addParamToUrl(formRequestUrl, 'searchform', 'true');
      }
      pageSize = this.lmFieldServiceAdapter.lmContextService.userDefinedStatesDefaultPageSize();
    }

    const dataView: LMDataView = this.getSearchFormValidatedDataView();
    const actionName = this.model.action;
    return this.lmFormRequestService.getUserDefinedStates(
      formRequestUrl, selectFieldId, dataView, actionName, pageSize, this.layoutObject.selectListInfo);
  }

  handleUserDefinedResponse(result: LMUserDefinedResponse): Array<LMStateValue> {
    let optionsAry;
    if (this.lmFieldServiceAdapter.lmContextService.userDefinedStatesPageSizeAndIndicator()) {
      if (result && result.hasOwnProperty('states') && Array.isArray(result.states)) {

        // Need to ensure that a selected value is in the options, even if it wasn't returned from the server call
        if (!this.modelField.isBlank) {
          if (!this.isMultiSelect) {
            // tslint:disable-next-line:triple-equals > using non-strict equality to evaluate 0, '0', 0.00, '0.00' as zero, etc
            // eslint-disable-next-line eqeqeq
            if (!result.states.some((value: LMStateValue) => value.value == this.modelField.value)) {
              if (this.modelField.states) {
                // have a selected value that isn't in the results array. Add it back in from the current modelField.state
                // triple-equals > using non-strict equality to evaluate 0, '0', 0.00, '0.00' as zero, etc
                // eslint-disable-next-line eqeqeq
                const selectedValue = this.modelField.states.filter((mfState: LMStateValue) => mfState.value == this.modelField.value);
                result.states = [ ... result.states, ... selectedValue ];
              }
            }
          } else {
            // multiselect case
            if (Array.isArray(this.modelField.value)) {

              const len = this.modelField.value.length;
              let selectedValues: Array<LMStateValue> = [];

              for (let i = 0; i < len; i++) {
                const selectedValue = this.modelField.value[ i ];
                // using non-strict equality to evaluate 0, '0', 0.00, '0.00' as zero, etc
                // eslint-disable-next-line eqeqeq
                const selectedStateValue = this.modelField.states.find((mfState: LMStateValue) => mfState.value == selectedValue);

                // have a selected value that isn't in the results array. Add it back in from the current modelField.state
                // triple-equals > using non-strict equality to evaluate 0, '0', 0.00, '0.00' as zero, etc
                // eslint-disable-next-line eqeqeq
                if (selectedStateValue && !result.states.some((resultValue: LMStateValue) => resultValue.value == selectedStateValue.value)) {
                  selectedValues = [ ... selectedValues, selectedStateValue ];
                }
              }

              result.states = [ ... result.states, ... selectedValues ];
            }
          }
        }

        if (this.isMultiSelect || (this.onSearchForm && !this.isUserDefined)) {
          optionsAry = this.lmFieldServiceAdapter.lmFormatUtilService.fieldStatesToMultiOptionsArray(result.states, this.modelField.value);

        } else {
          // need to format the zero value for BCD and PERCENT types as they need to match
          // the dataView field value for the track dirty processing to work correctly.
          // see also ViewBuilderBase.java > fieldAttributeToJsonObject()
          // and JSONConverter.java > fieldStateArrayForFieldValueNg()
          // and lm-model-field
          // using precision here as that is the property available for a state
          const zeroOrBlankValue = this.lmFieldServiceAdapter.lmFormatUtilService.fieldStateFormatZeroValue(this.specField.type, this.modelField.precision);
          optionsAry = this.lmFieldServiceAdapter.lmFormatUtilService.fieldStatesToOptionsArray(result.states, true, zeroOrBlankValue, this.isUserDefined);
        }

        const extraMessages: Array<LMStateValue> = [];

        if (this.isMultiSelect && !this.modelField.isKeyFieldMultiselect) {
          const selectMore: LMStateValue = {
            value: 'NANSelection',
            label: this.lmFieldServiceAdapter.lmFormatUtilService.formatMessage(Soho.Locale.translate('NumberSelected'), [ this.modelField.value.length, this.modelField.maxSelected ]),
            disabled: true
          };

          extraMessages.push(selectMore);
        }

        if (result.hasMore) {
          const searchToRefine: LMStateValue = {
            value: 'NANSearch',
            label: Soho.Locale.translate('RefineSearch'),
            disabled: true
          };

          extraMessages.push(searchToRefine);
          // this.modelField.states = [ ...optionsAry, searchToRefine];
        }

        this.modelField.states = [ ... optionsAry, ... extraMessages ];
      } else {
        // an error
        this.busyIndicator = false;
        this.lmFieldServiceAdapter.lmLoggerService.error([ 'Error in getUserDefinedStates', this.bindId, result ]);
      }
    } else {
      if (Array.isArray(result)) {
        if (this.isMultiSelect || (this.onSearchForm && this.isUserDefined)) {
          optionsAry = this.lmFieldServiceAdapter.lmFormatUtilService.fieldStatesToMultiOptionsArray(result, this.modelField.value);

        } else {
          // need to format the zero value for BCD and PERCENT types as they need to match
          // the dataView field value for the track dirty processing to work correctly.
          // see also ViewBuilderBase.java > fieldAttributeToJsonObject()
          // and JSONConverter.java > fieldStateArrayForFieldValueNg()
          // and lm-model-field
          // using precision here as that is the property available for a state
          const zeroOrBlankValue = this.lmFieldServiceAdapter.lmFormatUtilService.fieldStateFormatZeroValue(this.specField.type, this.modelField.precision);
          optionsAry = this.lmFieldServiceAdapter.lmFormatUtilService.fieldStatesToOptionsArray(result, true, zeroOrBlankValue, this.isUserDefined);
        }

        this.modelField.states = optionsAry;
      } else {
        // an error
        this.busyIndicator = false;
        this.lmFieldServiceAdapter.lmLoggerService.error([ 'Error in getUserDefinedStates', this.bindId, result ]);
      }
    }
    return optionsAry;
  }

  updateDropDownStates(): boolean {
    // Special case where the form field is read only. The user will not be able to click on the field to retrieve user defined states.
    // But an initial value / oto rule may come back with data. Fetch the states and update the control to display the value.
    if (this.isUserDefined && !this.modelField.isBlank && this.isReadOnly) {
      if (this.modelField.states === undefined || this.modelField.states.length === 0) {
        // no states
        return true;
      } else if ( this.modelField.states.length === 1) {
        // If there is only one state, is it the same as the model field value? Fetch if the values are not the same.
        return  this.modelField.value !== this.modelField.states[ 0 ].value;
      }
    }
    return false;
  }

  contextMenuClicked = ($event) => {
    this.onContextMenuEvent($event);
  }

  trackByValue(index: number, state: LMStateValue) {
    return state.value;
  }

  onBlur(event) {
    super.onBlur(event);
  }
}
